/*
 * Bootstrapper — wires the vendored habitat module to the plugin.
 *
 * Discovers per-component `habitat.js` (sync) and `habitat-async.js` (async
 * loader) files via webpack's require.context, registers them with the
 * vendored habitat module, and calls populate() to mount everything currently
 * in the DOM (plus install the MutationObservers for dynamic mounts).
 *
 * NOTE: existing per-component habitat.js / habitat-async.js files do NOT
 * need to be edited when picking up this change — the HTML contract
 * (data-sprintray-roi-calculator-component, data-prop-*, etc.) and the per-component
 * export shapes are preserved exactly.
 */
import {
	getDuplicateKeysString,
	objectsShareKey,
} from '@src/scripts/util/data';
import { toPairs } from '@src/scripts/util/functional';

import {
	register,
	unRegisterAll,
	disposeAll,
	populate,
	setComponentSelector,
} from './habitat';

const COMPONENT_SELECTOR = 'data-sprintray-roi-calculator-component';

const syncContextLoaders = () => [
	require.context(
		'scripts/components/',
		true,
		/scripts\/components\/.*habitat\.js$/
	),
];

const asyncContextLoaders = () => [
	require.context(
		'scripts/components/',
		true,
		/scripts\/components\/.*habitat-async\.js$/
	),
];

/*
 * Given webpack require.contexts, collect the default export of each matched
 * file into a list of objects. Each object is `{ ComponentName: value }`
 * where `value` is either a React component (sync) or a loader function
 * returning a Promise (async).
 */
const contextsToExports = ( contexts ) =>
	contexts.flatMap( ( context ) =>
		context.keys().map( ( key ) => context( key ).default )
	);

const warnOnDuplicates = ( allExports ) => {
	if ( process.env.NODE_ENV === 'production' ) return;
	if ( ! objectsShareKey( allExports ) ) return;
	const names = getDuplicateKeysString( allExports );
	// eslint-disable-next-line no-console
	console.error(
		`habitat: duplicate component names registered: ${ names }. ` +
			'Check your habitat.js and habitat-async.js files.'
	);
};

const registerAll = () => {
	const syncExports = contextsToExports( syncContextLoaders() );
	const asyncExports = contextsToExports( asyncContextLoaders() );

	warnOnDuplicates( [ ...syncExports, ...asyncExports ] );

	for ( const [ name, Component ] of syncExports.flatMap( toPairs ) ) {
		register( name, Component );
	}
	for ( const [ name, loader ] of asyncExports.flatMap( toPairs ) ) {
		register( name, loader, { async: true } );
	}
};

setComponentSelector( COMPONENT_SELECTOR );
registerAll();
populate();

if ( module.hot ) {
	// Re-register and re-populate when any habitat.js / habitat-async.js changes.
	const moduleIds = [ ...syncContextLoaders(), ...asyncContextLoaders() ].map(
		( c ) => c.id
	);
	module.hot.accept( moduleIds, () => {
		disposeAll();
		unRegisterAll();
		registerAll();
		populate();
	} );
}

/*
 * The public singleton. `update` and `dispose` are no-op shims kept for
 * compatibility with downstream code that may call `app.update()` /
 * `app.dispose()` (e.g. HMR hooks, dev console). These were part of
 * react-habitat's imperative API and are redundant here: the vendored habitat
 * module's MutationObserver handles dynamic mounts automatically, and internal
 * HMR uses `disposeAll` imported from `./habitat` directly.
 */
const warnNoOp = ( method ) => () => {
	if ( process.env.NODE_ENV !== 'production' ) {
		// eslint-disable-next-line no-console
		console.warn(
			`app.${ method }() is a no-op — the vendored habitat module ` +
				'handles mounts/unmounts automatically via MutationObserver. ' +
				'Remove the call (or use disposeAll() from ' +
				'@src/scripts/react-habitat/habitat for full teardown).'
		);
	}
};

const app = {
	update: warnNoOp( 'update' ),
	dispose: warnNoOp( 'dispose' ),
};

export default app;
